/**
 * @desc web 资源管理器oss文件上传
 * @author xos
 * @version 1.0.0
 */

import EventEmitter from '@remote/utils/event-emitter'
import { Base64 } from 'js-base64'
import { pathJoin } from '@remote/utils/file-helper'
import { logInfo } from '@remote/utils/logger'
import { getOSSClientConfig, updateOSSTaskStatus, getJobInfo, getOSSCompletedChunks, TGetOSSCompletedChunksResp } from '../api'
import global from '@remote/store/constantStore'
import { HEZI_API_URL } from '@remote/api/config'
import { chunkSize as CHUNK_SIZE } from '../enum'

import { FileSlicer, getAppropriateChunkSize } from '../helper'
// import { STATUS } from '../index.d'
import Events from '../enum'

enum STATUS {
  expried = -2,
  cancel = -1,   // 取消
  init,          // 初始中
  completed,     // 已完成
  process = 3,   // 处理中
  suspend        // 暂停
}

type TParams = {
  file: File
  chunkSize: number
  path: string
  deviceId: string
  token: string
  attachmentId: string
}

/**
 * oss文件上传器
 * @class
 * @constructor
 * @param { File } _file - 上传文件对象
 * @param { number } _chunkSize - 切片大小
 * @param { string } _path - 文件落盘路径
 */

class OSSUploader extends EventEmitter {
  private _status: STATUS
  public uploadOSSFinished: boolean
  private taskInfo: {
    attachmentId: string
    resourceId: string
    taskId: string
    fs: FileSlicer | null
  } | null
  private keepQueryTimer:NodeJS.Timer|null

  constructor () {
    super(
      [
        Events.UploadTaskError,
        Events.UploadTaskTransitServerProgress,
        Events.UploadTaskTransitServerCompleted,
        Events.UploadTaskDeviceReceiveProgress,
        Events.UploadTaskDeviceReceiveCompleted,
      ],
    )
    this._status = STATUS.init
    this.uploadOSSFinished = false
    this.taskInfo = null
    this.keepQueryTimer = null
  }

  // 初始化oss client实例
  createOSSClient (params: Pick<TParams, 'path' | 'deviceId' | 'chunkSize' | 'file'>): Promise<any[]> {
    return new Promise((resolve, reject) => {
      getOSSClientConfig({
        task: 'upload',
        deviceId: params.deviceId,
        path: pathJoin(params.path, params.file.name),
        size: params.file.size,
        chunkSize: params.chunkSize,
      }).then(res => {
        const [error, result] = res
        if (error) {
          return reject(error?.error_description || '获取oss配置文件失败')
        }
        if (result && result.code === 200 && result.data) {
          const ossConfig = result.data.ossRole
          const [region = 'oss-cn-shenzhen'] = ossConfig.endPoint.split('.')
          // @ts-ignore
          const client = new OSS({
            region: region,
            accessKeyId: ossConfig.accessKeyId,
            accessKeySecret: ossConfig.accessKeySecret,
            stsToken: ossConfig.securityToken,
            bucket: ossConfig.bucketName,
            refreshSTSToken: async () => {
              const [, ossConfigResp] = await getOSSClientConfig({
                task: 'upload',
                deviceId: params.deviceId,
                path: params.path,
                size: params.file.size,
                chunkSize: params.chunkSize,
              })
              return {
                accessKeyId: ossConfigResp?.data?.ossRole?.accessKeyId,
                accessKeySecret: ossConfigResp?.data?.ossRole?.accessKeySecret,
                stsToken: ossConfigResp?.data?.ossRole?.securityToken,
              }
            },
            refreshSTSTokenInterval: 60 * 60 * 1000,
          })
          resolve([client, { ...ossConfig, resourceId: result.data.resourceId, taskId: result.data.taskId }])
        } else {
          reject(result?.msg || '获取oss配置文件失败')
        }
      }).catch(reject)
    })
  }

  queryDeviceReceiveProgress (taskId: string, attachmentId: string, once?: boolean, keepQuery?:boolean) {
    let timer: NodeJS.Timeout | null = null
    const queryProgress = () => {
      getJobInfo({
        device_id: global.fromSpace,
        biz: 'deliver_upload',
        job_ids: [taskId],
      }).then(res => {
        const [error, result] = res
        if (error) {
          console.error(error)
        }
        if (result?.code === 200 && result.data) {
          const task = result.data?.list?.find(item => item.job_id === taskId)
          if (task) {
            const { progress } = task
            if (progress >= 100) {
              timer && clearInterval(timer)
              this.trigger(Events.UploadTaskDeviceReceiveCompleted, attachmentId)
            }
            this.trigger(Events.UploadTaskDeviceReceiveProgress, attachmentId, progress / 100)
          }
        }
      })
    }

    if (once) {
      queryProgress()
    } else {
      if (timer) clearInterval(timer)
      this.keepQueryTimer && clearInterval(this.keepQueryTimer)
      if ([STATUS.cancel, STATUS.suspend].includes(this._status) && !keepQuery) return timer && clearInterval(timer)
      timer = setInterval(() => {
        queryProgress()
      }, keepQuery ? 4000 : 8000)
      if (keepQuery ) this.keepQueryTimer = timer
    }
  }
  // 开始
  start (params: TParams) {
    this.OSSClientPutUpload(params)
  }

  makeCallback (ossConfig: { bucketName: string; objectPath: string; resourceId: string }, index: number, size: number) {
    const callbackJson = {
      callbackUrl: `${HEZI_API_URL}/xhub/callback`,

      callbackBody:
                `bucket=${ossConfig.bucketName}&` +
                'filename=' + encodeURIComponent(`${ossConfig.objectPath}/${ossConfig.resourceId}/${index}`) +
                `&size=${size}`,
      callbackBodyType: 'application/x-www-form-urlencoded',
    }

    return {
      'x-oss-callback': Base64.encode(JSON.stringify(callbackJson)),
    }
  }

  async OSSClientPutUpload (params: TParams) {
    const { attachmentId, file } = params
    const chunkSize = getAppropriateChunkSize(file.size) || CHUNK_SIZE * 10
    try {
      const [client, ossConfig] = await this.createOSSClient({ ...params, chunkSize: chunkSize })

      updateOSSTaskStatus({ taskId: ossConfig.taskId, status: 3 })

      const fs = new FileSlicer({ file, chunkSize: chunkSize, baseSlice: 0 })
      const _chunkTotal = fs.slices

      this.taskInfo = { fs, attachmentId, taskId: ossConfig.taskId, resourceId: ossConfig.resourceId }

      let restry = 1
      let interval = 0
      let isBreak = false

      const chunkUploadOSS = async (i: number, chunk: Blob) => {
        try {
          await client.put(
            `${ossConfig.objectPath}/${ossConfig.resourceId}/${i + 1}`,
            chunk,
            {
              headers: this.makeCallback(ossConfig, i + 1, file.size),
            },
          )
          this.trigger(Events.UploadTaskTransitServerProgress, attachmentId, i / _chunkTotal)
          if (interval === 3) {
            this.queryDeviceReceiveProgress(ossConfig.taskId, attachmentId, true)
            interval = 0
          } else {
            interval += 1
          }
        } catch (err) {
          if (restry < 3) {
            restry += 1
            chunkUploadOSS(i, chunk)
          } else {
            throw err
          }
        }
      }

      for (let i = 0; i < _chunkTotal; ++i) {
        if ([STATUS.cancel, STATUS.suspend].includes(this._status)) {
          updateOSSTaskStatus({ taskId: ossConfig.taskId, status: this._status })
          return
        }
        restry = 1
        let chunk = fs.getNextSlice() as Blob
        try {
          await chunkUploadOSS(i, chunk)
        } catch (err) {
          this.trigger(Events.UploadTaskError, attachmentId, '服务器连接异常')
          logInfo('upload_oss', {
            action: 'upload_oss',
            desc: '客户端大文件上传至oss error',
          })
          isBreak = true
          break
        } finally {
          // @ts-ignore
          chunk = null
        }
      }

      if (!isBreak) {
        this.trigger(Events.UploadTaskTransitServerCompleted, attachmentId)
        fs.destroy()
        this.taskInfo.fs = null
        this.uploadOSSFinished = true
        updateOSSTaskStatus({ taskId: ossConfig.taskId, status: 1 })
        this.queryDeviceReceiveProgress(ossConfig.taskId, attachmentId)
      }
    } catch (err) {
      this.trigger(Events.UploadTaskError, attachmentId, '服务器连接失败')
    }
  }

  // 续传
  resume () {
    if (!this.taskInfo?.taskId) return console.warn('续传失败未查询到任务')
    this._status = STATUS.process
    if (this.uploadOSSFinished && this.taskInfo) {
      // 只做查询
      updateOSSTaskStatus({ taskId: this.taskInfo?.taskId, status: 2 })
      this.queryDeviceReceiveProgress(this.taskInfo?.taskId, this.taskInfo?.attachmentId)
    } else {
      // 从断开处重传
      if (this.taskInfo?.resourceId) {
        const handleCreateOSSClient = (resp) => {
          const [error, res] = resp
          if (res && res.code === 200 && res.data) {
            const { complete, ossRole: ossConfig, list: completedList } = res.data

            const [region = 'oss-cn-shenzhen'] = ossConfig.endPoint.split('.')
            // @ts-ignore
            const client = new OSS({
              region: region,
              accessKeyId: ossConfig.accessKeyId,
              accessKeySecret: ossConfig.accessKeySecret,
              stsToken: ossConfig.securityToken,
              bucket: ossConfig.bucketName,
              refreshSTSToken: async () => {
                const [error, ossConfigResp] = await getOSSCompletedChunks(this.taskInfo?.resourceId as string)
                if (error) {
                  return Promise.reject(error?.error_description || '文件重传获取oss配置失败')
                }
                return {
                  accessKeyId: ossConfigResp?.data?.ossRole?.accessKeyId,
                  accessKeySecret: ossConfigResp?.data?.ossRole?.accessKeySecret,
                  stsToken: ossConfigResp?.data?.ossRole?.securityToken,
                }
              },
              refreshSTSTokenInterval: 60 * 60 * 1000,
            })
            return Promise.resolve([client, { ...ossConfig, complete, completedList }])
          } else {
            return Promise.reject(error?.error_description || res?.msg || '文件重传获取oss配置失败')
          }
        }

        const putChunckByOSSClient = async (res: any[]) => {
          const [client, { complete, completedList, ...ossConfig }] = res
          const { fs, attachmentId, taskId, resourceId } = this.taskInfo!

          if (complete) {
            this.trigger(Events.UploadTaskTransitServerCompleted, attachmentId)
            return
          }

          updateOSSTaskStatus({ taskId: taskId, status: 3 })
          const _chunkTotal = fs?.slices as number

          this.taskInfo = { fs, attachmentId, taskId: taskId, resourceId: resourceId }

          for (let i = 0; i < _chunkTotal; ++i) {
            // 用户主动暂停或取消
            if ([STATUS.cancel, STATUS.suspend].includes(this._status)) {
              updateOSSTaskStatus({ taskId: taskId, status: this._status })
              return
            }
            // 该分片已上传
            if (completedList.includes(i + 1)) continue

            try {
              const chunk = fs?.getNextSlice() as Blob
              await client.put(
                `${ossConfig.objectPath}/${resourceId}/${i + 1}`,
                chunk,
                {
                  headers: this.makeCallback({ ...ossConfig, resourceId }, i + 1, fs?.size as number),
                },
              )
              this.trigger(Events.UploadTaskTransitServerProgress, attachmentId, i / _chunkTotal)
              this.queryDeviceReceiveProgress(taskId, attachmentId, true)
            } catch (err) {
              console.log('OSSClientPutUpload err', err)
              this.trigger(Events.UploadTaskError, attachmentId, '服务器连接异常')
              break
            }
          }

          this.trigger(Events.UploadTaskTransitServerCompleted, attachmentId)
          this.taskInfo.fs = null
          this.uploadOSSFinished = true
          updateOSSTaskStatus({ taskId: taskId, status: 1 })
          this.queryDeviceReceiveProgress(taskId, attachmentId)
        }

        getOSSCompletedChunks(this.taskInfo?.resourceId)
          .then(handleCreateOSSClient)
          .then(putChunckByOSSClient)
          .catch(err => {
            this.trigger(Events.UploadTaskError, this.taskInfo?.attachmentId, JSON.stringify(err))
          })
      }
    }
  }

  // 暂停
  pause (keepQuery?:boolean) {
    this._status = STATUS.suspend
    updateOSSTaskStatus({ taskId: this.taskInfo?.taskId as string, status: STATUS.suspend })
    console.log('call pause:', keepQuery, this.taskInfo)
    if (keepQuery && this.taskInfo?.taskId && this.taskInfo?.attachmentId) {
      this.queryDeviceReceiveProgress(this.taskInfo.taskId, this.taskInfo.attachmentId, false, true)
    }
  }
  // 取消
  cancel () {
    updateOSSTaskStatus({ taskId: this.taskInfo?.taskId as string, status: STATUS.cancel })
    this._status = STATUS.cancel
  }

  destroy () {
    this.removeAllEventListener()
    this.taskInfo = null
  }
}

export default OSSUploader
